import React, {FC, ReactElement, useEffect, useMemo, useState} from 'react';
import LoaderWithCompanySelected from "../../components/LoaderWithCompanySelected";
import {CustomerDto} from "../../model/customer/Customer";
import {customerApi} from "../../backend/CustomerApi";
import {Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem} from "@mui/material";
import {Formik} from "formik";
import {FormikProps} from "formik/dist/types";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import TextFieldWithValidation from "../../components/input/TextFieldWithValidation";
import CountrySelector from "../../components/selector/CountrySelector";
import LoadingButton from "../../components/button/LoadingButton";
import {customersStore} from "../../store/CustomersStore";
import * as Yup from "yup";
import {Trans, useTranslation} from "react-i18next";
import CurrencySelector from "../../components/CurrencySelector";

export interface IProps {
    customerId?: string;
    open: boolean;
    onCancel?: () => void
    onSave?: (changeCustomer: CustomerDto) => void
}

const useValidationSchema = () => {
    const {t} = useTranslation();
    return useMemo(() => {
        return Yup.lazy((values: CustomerDto) => {
            let scheme: any = {
                name: Yup.string().trim().required(t("common.error.required_field")),
                countryCode: Yup.string().trim().required(t("common.error.required_field")),
                paymentDeadLine: Yup.number().moreThan(0, t("common.error.should_be_greater_that_zero")).required(t("common.error.required_field")),
                overdueCharge: Yup.number().moreThan(0, "common.error.should_be_greater_that_zero").required(t("common.error.required_field")),
                email: Yup.string().email(t("common.error.invalid_email")),
            };
            if (values.countryCode === 'EE') {
                scheme = {
                    ...scheme,
                    regNumber: values.notTDCustomer ? Yup.string() : Yup.string().required(t("common.error.required_field")),
                    address: Yup.string().required(t("common.error.required_field")),
                    city: Yup.string().required(t("common.error.required_field")),
                    postalCode: Yup.string().required(t("common.error.required_field")),
                }
            }
            return Yup.object(scheme);
        });
    }, [t])
}

const EditCustomerDialog: FC<IProps> = (props): ReactElement => {
    const [loading, setLoading] = useState(true);
    const [error,] = useState(false);
    const [customer, setCustomer] = useState<CustomerDto>({});

    useEffect(() => {
        const customerId = props.customerId;
        if (customerId) {
            customerApi.fetchCustomer(customerId)
                    .then(data => {
                        setCustomer(data)
                        setLoading(false)
                    })
        } else {
            setCustomer({
                countryCode: "EE",
                defaultCurrency: "EUR",
                invoiceLang: "EN",
                paymentDeadLine: 10,
                overdueCharge: "0.05"
            })
            setLoading(false)
        }
    }, [props.customerId])

    const saveAndClose = async (changed: CustomerDto) => {
        const updatedCustomer = await customerApi.saveCustomer(changed);
        await customersStore.invalidate();
        await customersStore.refresh();
        props.onSave?.(updatedCustomer);
    }

    const handleClose = () => {
        props.onCancel?.();
    }

    const validationSchema = useValidationSchema();

    return <>
        <Dialog open={props.open} onClose={handleClose} fullWidth={true} scroll="paper">
            <LoaderWithCompanySelected loading={loading} error={error}>
                <Formik enableReinitialize initialValues={customer} onSubmit={saveAndClose} validationSchema={validationSchema}>
                    {(props: FormikProps<CustomerDto>) => <DialogBody formik={props} handleClose={handleClose}/>}
                </Formik>
            </LoaderWithCompanySelected>
        </Dialog>
    </>
}

const DialogBody = ({formik, handleClose}: { formik: FormikProps<CustomerDto>, handleClose: () => void }) => {
    const customerId = formik.values.id;
    const existingClient = !!customerId
    const {t} = useTranslation();
    return <>
        <DialogTitle id="max-width-dialog-title">
            {existingClient ?
                    <Trans i18nKey="edit_customer.edit_header">Edit client</Trans> :
                    <Trans i18nKey="edit_customer.add_header">Add client</Trans>
            }
        </DialogTitle>
        <DialogContent>
            <Grid container spacing={1}>
                <Grid xs={12} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="name"
                            label={t("edit_customer.name.label", "Name")}
                            placeholder={t("edit_customer.name.placeholder", "Full name")}
                            helperText={t("edit_customer.name.helper_text", "Company name or private person's full name")}
                            value={formik.values.name}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.name}
                    />
                </Grid>
                <Grid xs={12} item>
                    <FormControlLabel
                            control={<Checkbox checked={formik.values.notTDCustomer}
                                               disabled={existingClient}
                                               onChange={e => {
                                                   const value = e.target.checked;
                                                   formik.setFieldValue("notTDCustomer", value, true)
                                               }}
                                               name="notTDCustomer"
                                               color="primary"/>
                            }
                            label={t("edit_customer.not_td_customer", "Private person or enterprise registered outside of Estonia")}
                    />
                </Grid>


                <Grid xs={6} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="regNumber"
                            label={t("edit_customer.reg_num.label", "Reg. number")}
                            placeholder={t("edit_customer.reg_num.placeholder", "Registration number")}
                            value={formik.values.regNumber}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.regNumber}
                    />
                </Grid>
                <Grid xs={6} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="vatNumber"
                            label={t("edit_customer.vat_num.label", "VAT number")}
                            placeholder={t("edit_customer.vat_num.placeholder", "VAT number")}
                            value={formik.values.vatNumber}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.vatNumber}
                    />
                </Grid>

                <Grid xs={12} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="address"
                            label={t("edit_customer.address.label", "Address")}
                            placeholder={t("edit_customer.address.placeholder", "Address")}
                            helperText={t("edit_customer.address.helper_text", "Address line (street, house number, etc)")}
                            value={formik.values.address}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.address}
                    />
                </Grid>

                <Grid xs={6} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="city"
                            label={t("edit_customer.city", "City")}
                            placeholder={t("edit_customer.city", "City")}
                            value={formik.values.city}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.city}
                    />
                </Grid>
                <Grid xs={6} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="postalCode"
                            label={t("edit_customer.postal_code", "Postal code")}
                            placeholder={t("edit_customer.postal_code", "Postal code")}
                            value={formik.values.postalCode}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.postalCode}
                    />
                </Grid>

                <Grid xs={12} item>
                    <CountrySelector
                            label={t("edit_customer.country", "Country")}
                            placeholder={t("edit_customer.country", "Country")}
                            value={formik.values.countryCode}
                            hasError={formik.errors.countryCode}
                            onChanged={code => formik.setFieldValue("countryCode", code, true)}
                    />
                </Grid>

                <Grid xs={6} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="email"
                            label={t("edit_customer.email", "Email")}
                            placeholder={t("edit_customer.email", "Email")}
                            value={formik.values.email}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.email}
                    />
                </Grid>
                <Grid xs={6} item>
                    <TextFieldWithValidation
                            autoComplete="off"
                            name="phoneNo"
                            label={t("edit_customer.phone_num", "Phone number")}
                            placeholder={t("edit_customer.phone_num", "Phone number")}
                            value={formik.values.phoneNo}
                            fullWidth
                            onChange={formik.handleChange}
                            hasError={formik.errors.phoneNo}
                    />
                </Grid>

                <Grid xs={6} item>
                    <CurrencySelector
                            disabled={existingClient}
                            onChange={code => formik.setFieldValue("defaultCurrency", code, true)}
                            value={formik.values.defaultCurrency}
                            label={t("edit_customer.currency", "Currency")}
                            fullWidth={true}
                            hasError={formik.errors.defaultCurrency}
                    />
                </Grid>
                <Grid xs={6} item>
                    <TextFieldWithValidation
                            onChange={formik.handleChange}
                            select={true}
                            name="invoiceLang"
                            value={formik.values.invoiceLang}
                            label={t("edit_customer.inv_lang", "Invoice language")}
                            fullWidth={true}
                            hasError={formik.errors.invoiceLang}
                    >
                        <MenuItem value="EN">English</MenuItem>
                        <MenuItem value="ET">Estonian</MenuItem>
                        <MenuItem value="RU">Russian</MenuItem>
                    </TextFieldWithValidation>
                </Grid>

                <Grid xs={6} item>
                    <TextFieldWithValidation
                            disabled={existingClient}
                            autoComplete="off"
                            name="paymentDeadLine"
                            label={t("edit_customer.payment_dead_line.label", "Payment dead line")}
                            placeholder={t("edit_customer.payment_dead_line.placeholder", "Payment dead line")}
                            helperText={t("edit_customer.payment_dead_line.helper_text", "Number of days given to customer to pay the invoice")}
                            value={formik.values.paymentDeadLine}
                            fullWidth
                            type="number"
                            onChange={formik.handleChange}
                            hasError={formik.errors.paymentDeadLine}
                    />
                </Grid>
                <Grid xs={6} item>
                    <TextFieldWithValidation
                            disabled={existingClient}
                            autoComplete="off"
                            name="overdueCharge"
                            label={t("edit_customer.overdue_charge.label", "Overdue charge")}
                            placeholder={t("edit_customer.overdue_charge.placeholder", "Overdue charge")}
                            helperText={t("edit_customer.overdue_charge.helper_text", "Overdue charge in %")}
                            value={formik.values.overdueCharge}
                            fullWidth
                            type="number"
                            onChange={formik.handleChange}
                            hasError={formik.errors.overdueCharge}
                    />
                </Grid>

            </Grid>
        </DialogContent>
        <DialogActions>
            <Button color="secondary" variant={"outlined"} onClick={handleClose}
                    startIcon={<CancelIcon/>}><Trans i18nKey="common.close">Close</Trans></Button>
            <LoadingButton onClick={formik.submitForm} variant="outlined" startIcon={<SaveAltIcon/>}
                           color="primary"><Trans i18nKey="common.save">Save</Trans></LoadingButton>
        </DialogActions>
    </>
}

export default EditCustomerDialog;